/**
 * Holiday start/end values are naive local strings — 'YYYY-MM-DD HH:mm', no timezone.
 *
 * The client admin sends exactly that (admin-master HolidayCreatePopup.tsx: `${date} 00:00`), the
 * API stores it in a TIMESTAMP WITHOUT TIME ZONE column, and oitchau-holidays returns it without an
 * offset, as 'YYYY-MM-DDTHH:mm:ss'.
 *
 * react-admin's date inputs round-trip their value through `new Date()`, so `JSON.stringify`
 * serialised it as UTC: '2027-11-12T00:00' entered in Kyiv went out as '2027-11-11T22:00:00.000Z'.
 * The API kept the wall-clock and dropped the offset, moving the holiday onto the previous day, and
 * every re-save shifted it again. [PROD-28995]
 *
 * These helpers keep the value a string from end to end. Nothing here builds a Date from a string,
 * so the browser timezone cannot leak into what we send or show.
 */

// 'YYYY-MM-DD', optionally followed by 'HH:mm', seconds and fractions ('2024-11-20 00:00:00.000',
// '2027-11-11T22:00:00'). An offset ('...Z', '...+02:00') is accepted and ignored — the wall-clock is
// kept, which is the contract the rest of the holiday stack follows. Anchored at both ends so a
// malformed value is rejected rather than half-read.
const NAIVE_DATE_TIME = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}(?::?\d{2})?)?)?$/;

const pad = (value: number, length: number) => String(value).padStart(length, '0');

// Plain arithmetic rather than Date.UTC, which also maps years 0-99 onto 1900-1999.
const isLeapYear = (year: number) => year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
const DAYS_IN_MONTH = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
const daysInMonth = (year: number, month: number) => (month === 2 && isLeapYear(year) ? 29 : DAYS_IN_MONTH[month - 1]);

const isRealDateTime = (year: number, month: number, day: number, hour: number, minute: number) => month >= 1 && month <= 12 && day >= 1 && day <= daysInMonth(year, month) && hour <= 23 && minute <= 59;

const parts = (value: unknown): { date: string; time: string } | null => {
  if (value == null || value === '') return null;
  // A Date only reaches here from a defaultValue; read its local parts, which is the wall-clock the
  // user sees in the picker.
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    return {
      date: `${pad(value.getFullYear(), 4)}-${pad(value.getMonth() + 1, 2)}-${pad(value.getDate(), 2)}`,
      time: `${pad(value.getHours(), 2)}:${pad(value.getMinutes(), 2)}`
    };
  }
  if (typeof value !== 'string') return null;
  const match = NAIVE_DATE_TIME.exec(value);
  if (!match) return null;
  const [, year, month, day, hour = '00', minute = '00'] = match;
  // The pickers cannot produce '2027-13-45' or '25:99', but a corrupt API value could.
  if (!isRealDateTime(Number(year), Number(month), Number(day), Number(hour), Number(minute))) return null;
  return { date: `${year}-${month}-${day}`, time: `${hour}:${minute}` };
};

/** `format` for DateInput, and the display format for a whole-day holiday: 'YYYY-MM-DD'. */
export const formatNaiveDate = (value: unknown): string => parts(value)?.date ?? '';

/** `format` for DateTimeInput: the 'YYYY-MM-DDTHH:mm' an <input type="datetime-local" /> expects. */
export const formatNaiveDateTimeInput = (value: unknown): string => {
  const p = parts(value);
  return p ? `${p.date}T${p.time}` : '';
};

/** Display format for a timed holiday: 'YYYY-MM-DD HH:mm'. */
export const formatNaiveDateTime = (value: unknown): string => {
  const p = parts(value);
  return p ? `${p.date} ${p.time}` : '';
};

/** Display a holiday start/end: the day for a whole-day holiday, day and time for an event. */
export const formatHolidayValue = (value: unknown, allDay: unknown): string => (allDay ? formatNaiveDate(value) : formatNaiveDateTime(value)) || '—';

/**
 * `parse` for DateInput: a whole-day holiday is stored at midnight, byte-identical to what the
 * client admin sends. Empty or half-typed input becomes null so the form reports it missing.
 */
export const parseNaiveDateInput = (value: unknown): string | null => {
  const p = parts(value);
  return p ? `${p.date} 00:00` : null;
};

/** `parse` for DateTimeInput: keep the picked wall-clock as a naive 'YYYY-MM-DD HH:mm' string. */
export const parseNaiveDateTimeInput = (value: unknown): string | null => {
  const p = parts(value);
  return p ? `${p.date} ${p.time}` : null;
};
